import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { ensurePostHogInit } from './posthog-init';

/**
 * Runtime feature flags served by PostHog. Complements — does not replace —
 * the build-time env flags in `src/shared/lib/config/feature-flags.ts`:
 *
 * - env flags: infra/build-affecting toggles; flipping needs a deploy
 * - runtime flags: % rollouts, cohort/user targeting, kill switches,
 *   experiment variants; flipped in the PostHog UI with no deploy
 *
 * Never pass a raw string to the hooks — register the key here first
 * (snake_case, same registry discipline as events.ts).
 */
export const RuntimeFlag = Object.freeze({
  /**
   * Rollout + kill switch: tus parallel partial uploads for fresh
   * single-file studio uploads (bad-route throughput mitigation).
   * Boolean `true` → 4 streams; string variant → stream count ('2'…'8').
   * `undefined`/`false` → serial (control). Verified against Bunny's
   * presigned TUS flow 2026-08-13: 1.49GB, 4 streams, concat accepted,
   * playback complete with exact duration.
   */
  UPLOAD_PARALLEL_STREAMS: 'upload_parallel_streams',
  /**
   * Rollout + kill switch: What's New (announcements popup, /whats-new page,
   * rail New dot). Boolean. Team-only cohort first — prod is the only BE env,
   * so test announcements are published live. `undefined` → hidden, except
   * in local dev where PostHog is off (see `useWhatsNewEnabled`).
   */
  WHATS_NEW: 'whats_new',
} as const);

export type RuntimeFlagKey = (typeof RuntimeFlag)[keyof typeof RuntimeFlag];

interface UseRuntimeFlagOptions {
  /**
   * When true, the flag is never read and the hook stays `undefined`.
   * Use this to avoid recording a `$feature_flag_called` exposure on render
   * paths where the variant is not actually applied — experiments count
   * every read as an exposure.
   */
  skip?: boolean;
}

/**
 * Current variant (string) or enabled state (boolean) of a runtime flag.
 *
 * Returns `undefined` while flags load, in dev (PostHog is fully disabled),
 * or when the flag doesn't exist — callers MUST render the control/default
 * experience for `undefined`. posthog-js caches flag values in localStorage,
 * so only a person's first-ever visit resolves late; still, never gate the
 * static shell or chrome on a runtime flag (Cache Components — flags resolve
 * client-side after hydration).
 */
export function useRuntimeFlag(
  key: RuntimeFlagKey,
  { skip = false }: UseRuntimeFlagOptions = {}
): string | boolean | undefined {
  const [value, setValue] = useState<string | boolean | undefined>(undefined);

  useEffect(() => {
    if (skip) return;
    ensurePostHogInit();
    if (!posthog.__loaded) return;
    const update = () => setValue(posthog.getFeatureFlag(key));
    update();
    return posthog.onFeatureFlags(update);
  }, [key, skip]);

  return value;
}
